<template>
  <div class="card mb-3">
    <div class="card-body">

      <!-- Header -->
      <div class="d-flex flex-column justify-content-between mb-3">
        <h3 class="h3 mb-3">{{ label }}</h3>
        <div>
          <button
            type="button"
            class="btn btn-primary btn-sm mr-2"
            :disabled="isRunning || startLoading"
            @click="start"
          >
            <span
              v-if="startLoading"
              class="spinner-border spinner-border-sm mr-1"
              role="status"
              aria-hidden="true"
            ></span>
            Run
          </button>
          <button
            v-if="statusData.has_file && !isRunning"
            type="button"
            class="btn btn-outline-primary btn-sm mr-2"
            :disabled="downloadLoading"
            @click="download"
          >
            <span
              v-if="downloadLoading"
              class="spinner-border spinner-border-sm mr-1"
              role="status"
              aria-hidden="true"
            ></span>
            Download Excel
          </button>
          <button
            v-if="status && status !== 'idle' && !isRunning"
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="resetLoading"
            @click="reset"
          >
            <span
              v-if="resetLoading"
              class="spinner-border spinner-border-sm mr-1"
              role="status"
              aria-hidden="true"
            ></span>
            Clear
          </button>
        </div>
      </div>

      <!-- Status row -->
      <div v-if="status && status !== 'idle'" class="d-flex align-items-center flex-wrap mb-2">
        <span :class="['badge mr-2 mb-1', badgeClass]" style="font-size: 0.85rem; padding: 0.35em 0.65em">
          {{ status }}
        </span>
        <span v-if="statusData.started_at" class="text-muted small mr-3 mb-1">
          Started: {{ formatDate(statusData.started_at) }}
        </span>
        <span v-if="statusData.finished_at" class="text-muted small mb-1">
          Finished: {{ formatDate(statusData.finished_at) }}
        </span>
      </div>

      <!-- Progress bar -->
      <div v-if="status === 'running' || status === 'pending' || status === 'done'" class="mb-3">
        <div class="d-flex justify-content-between mb-1">
          <small class="text-muted">
            Processed: {{ statusData.processed || 0 }} / {{ statusData.total || 0 }}
          </small>
          <small class="text-muted">{{ progressPercent }}%</small>
        </div>
        <div class="progress" style="height: 18px; border-radius: 4px">
          <div
            class="progress-bar"
            :class="status === 'done' ? 'bg-success' : 'progress-bar-striped progress-bar-animated'"
            role="progressbar"
            :style="{ width: `${progressPercent}%` }"
            :aria-valuenow="progressPercent"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <span v-if="progressPercent > 10">{{ progressPercent }}%</span>
          </div>
        </div>
      </div>

      <!-- Failed status -->
      <div v-if="status === 'failed'" class="alert alert-danger mb-2 py-2">
        <strong>Error:</strong> {{ statusData.error || 'Unknown error' }}
      </div>

      <!-- Failed items -->
      <div v-if="statusData.failed && statusData.failed.length > 0">
        <p class="mb-1 small">
          <strong class="text-danger">Failed items: {{ statusData.failed.length }}</strong>
        </p>
        <div style="max-height: 600px; overflow-y: auto;">
          <table class="table table-sm table-bordered mb-0">
            <thead class="thead-light" style="position: sticky; top: 0; z-index: 1;">
              <tr>
                <th>ID</th>
                <th>Key</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in statusData.failed" :key="`${item.id}-${item.key}`">
                <td>{{ item.id }}</td>
                <td>{{ item.key }}</td>
                <td>{{ item.reason }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Polling indicator -->
      <p v-if="isRunning" class="text-muted small mb-0 mt-2">
        <span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
        Checking every 2 seconds…
      </p>

    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api'
import useImportCard from '@/composables/useImportCard'

export default defineComponent({
  name: 'ImportCard',
  props: {
    type: { type: String, required: true },
    label: { type: String, required: true }
  },
  setup(props) {
    const { status, statusData, startLoading, resetLoading, downloadLoading, isRunning, progressPercent, start, reset, download } = useImportCard(props.type)

    const badgeClass = computed(() => {
      const map = {
        idle: 'badge-secondary',
        pending: 'badge-warning',
        running: 'badge-primary',
        done: 'badge-success',
        failed: 'badge-danger'
      }
      return map[status.value] != null ? map[status.value] : 'badge-secondary'
    })

    function formatDate(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleString()
    }

    return {
      status,
      statusData,
      startLoading,
      resetLoading,
      downloadLoading,
      isRunning,
      progressPercent,
      badgeClass,
      start,
      reset,
      download,
      formatDate
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/components/SuperAdmin/ImportCard.vue